import { ThemeEvents } from '@theme/events';

// These enhancements keep the native product form, variant updates and cart submissions.
const openSheets = new Set();
const normalize = (value = '') => value.replaceAll('İ', 'i').toLowerCase().trim();
const sizeNames = new Set(['size', 'sizes', 'beden', 'bedenler', 'beden seçeneği', 'boyut', 'boyutlar', 'boyut seçeneği']);

class RSProductExtras extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('.shopify-section');
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.addEventListener('click', this.handleClick, { signal });
    this.addEventListener('cancel', this.handleCancel, { signal, capture: true });
    this.addEventListener('close', this.handleClose, { signal, capture: true });
    this.addEventListener('keydown', this.handleKey, { signal });
    this.scope?.addEventListener('click', this.handleGuide, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.refreshCart, { signal });
    document.addEventListener(ThemeEvents.cartError, this.refreshCart, { signal });
    this.observer = new MutationObserver(this.queueEnhance);
    if (this.scope) this.observer.observe(this.scope, { childList: true, subtree: true });
    this.enhance();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.cartAbort?.abort();
    this.observer?.disconnect();
    this.removeGuideButtons();
    this.releaseSheet(false);
  }

  queueEnhance = () => {
    if (this.queued) return;
    this.queued = true;
    queueMicrotask(() => {
      this.queued = false;
      if (this.isConnected) this.enhance();
    });
  };

  enhance() {
    if (this.openDialog && !this.openDialog.isConnected) this.releaseSheet(false);
    this.dialog = this.querySelector('[data-rs-product-sheet]');
    const guide = this.querySelector('[data-rs-sheet-detail="size"]');
    const picker = [...(this.scope?.querySelectorAll('variant-picker') || [])].find(
      (item) => item.dataset.productId === this.dataset.productId && !item.closest('dialog')
    );
    const option = [...(picker?.querySelectorAll('[data-rs-option-name]') || [])].find(
      (item) => sizeNames.has(normalize(item.dataset.rsOptionName))
    );
    const target = option?.querySelector('legend') || option;
    this.removeGuideButtons(target);
    if (guide && target && this.dialog?.showModal) {
      let button = [...target.querySelectorAll('[data-rs-guide-for]')].find(
        (item) => item.dataset.rsGuideFor === this.dataset.blockId
      );
      if (!button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'rs-product-size-guide';
        button.dataset.rsGuideFor = this.dataset.blockId;
        button.textContent = guide.dataset.sheetTitle;
        button.setAttribute('aria-haspopup', 'dialog');
        target.append(button);
      }
      this.dataset.guideLinked = 'true';
    } else {
      delete this.dataset.guideLinked;
    }
    if (this.lastCart) this.renderShipping(this.lastCart);
  }

  removeGuideButtons(retainTarget) {
    for (const button of this.scope?.querySelectorAll('[data-rs-guide-for]') || []) {
      if (button.dataset.rsGuideFor === this.dataset.blockId && !retainTarget?.contains(button)) button.remove();
    }
  }

  handleGuide = (event) => {
    const button = event.target.closest?.('[data-rs-guide-for]');
    if (!button || button.dataset.rsGuideFor !== this.dataset.blockId) return;
    event.preventDefault();
    this.openSheet(this.querySelector('[data-rs-sheet-detail="size"]'), button);
  };

  handleClick = (event) => {
    if (event.target.closest?.('[data-rs-sheet-close]')) {
      this.dialog?.close();
      return;
    }
    const summary = event.target.closest?.('[data-rs-sheet-detail] > summary');
    if (summary && this.contains(summary) && this.dialog?.showModal) {
      event.preventDefault();
      this.openSheet(summary.parentElement, summary);
    }
    if (event.target === this.openDialog) {
      const rect = this.openDialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.openDialog.close();
    }
  };

  openSheet(detail, trigger) {
    if (!detail || !this.dialog?.showModal || this.dialog.open) return;
    const body = this.dialog.querySelector('[data-rs-sheet-body]');
    const source = detail.querySelector('[data-rs-sheet-content]');
    if (!body || !source) return;
    this.dialog.querySelector('[data-rs-sheet-title]').textContent = detail.dataset.sheetTitle;
    body.replaceChildren(...[...source.childNodes].map((node) => node.cloneNode(true)));
    this.trigger = trigger;
    detail.open = false;
    this.dialog.showModal();
    this.openDialog = this.dialog;
    openSheets.add(this);
    document.documentElement.classList.add('rs-product-sheet-open');
    body.scrollTop = 0;
    this.dialog.querySelector('[data-rs-sheet-close]')?.focus({ preventScroll: true });
  }

  handleCancel = (event) => {
    if (event.target !== this.openDialog) return;
    // Let the native dialog's Escape close action run and use the close event for cleanup.
  };

  handleClose = (event) => {
    if (event.target === this.openDialog) this.releaseSheet(true);
  };

  releaseSheet(restoreFocus) {
    openSheets.delete(this);
    if (!openSheets.size) document.documentElement.classList.remove('rs-product-sheet-open');
    this.openDialog = null;
    if (restoreFocus && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    this.trigger = null;
  }

  handleKey = (event) => {
    if (!this.openDialog?.open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.openDialog.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...this.openDialog.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')]
      .filter((item) => !item.closest('[hidden]') && item.getClientRects().length);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first) { event.preventDefault(); return; }
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  refreshCart = async () => {
    if (!this.querySelector('[data-rs-shipping]')) return;
    this.cartAbort?.abort();
    const controller = new AbortController();
    this.cartAbort = controller;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root.replace(/\/?$/, '/')}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (controller.signal.aborted || !this.isConnected) return;
      this.lastCart = cart;
      this.renderShipping(cart);
    } catch (error) {
      // An uncertain total must not keep promising that the free-shipping threshold is met.
      if (error.name !== 'AbortError' && this.isConnected) {
        this.lastCart = null;
        this.querySelector('[data-rs-shipping]')?.setAttribute('hidden', '');
      }
    }
  };

  renderShipping(cart) {
    const widget = this.querySelector('[data-rs-shipping]');
    if (!widget) return;
    const threshold = Number(widget.dataset.threshold);
    const currency = widget.dataset.currency;
    const valid = threshold > 0 && Number.isFinite(threshold) && Number.isFinite(cart.total_price) && cart.total_price >= 0 && cart.currency === currency;
    widget.hidden = !valid;
    if (!valid) return;
    const remaining = Math.max(0, threshold - cart.total_price);
    let amount;
    try {
      amount = new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', { style: 'currency', currency }).format(remaining / 100);
    } catch { widget.hidden = true; return; }
    const message = remaining === 0
      ? widget.dataset.completeMessage
      : widget.dataset.remainingMessage.replaceAll('[amount]', amount);
    const label = widget.querySelector('[data-rs-shipping-message]');
    if (label.textContent !== message) label.textContent = message;
    widget.querySelector('progress').value = Math.min(100, cart.total_price / threshold * 100);
  }
}

if (!customElements.get('rs-product-extras')) customElements.define('rs-product-extras', RSProductExtras);

